import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // --- NEW: GLOBAL SELECTORS STATE ---
  const [preferences, setPreferences] = useState({
    theme: 'light',
    language: 'en',
    currency: 'NGN'
  });

  useEffect(() => {
    // Check for saved user session
    const savedUser = localStorage.getItem('agritrade_user');
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('agritrade_user');
      }
    }

    // --- NEW: LOAD SAVED PREFERENCES ---
    const savedPrefs = localStorage.getItem('agritrade_prefs');
    if (savedPrefs) {
      try {
        setPreferences(JSON.parse(savedPrefs));
      } catch (e) {
        console.error('Error parsing preferences');
      }
    }
    
    setLoading(false);
  }, []);

  const login = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('agritrade_user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('agritrade_user');
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('agritrade_user', JSON.stringify(updatedUserData));
  };

  // --- NEW: UPDATE PREFERENCES & SYNC TO BACKEND ---
  const updatePreferences = async (newPrefs) => {
    const updated = { ...preferences, ...newPrefs };
    setPreferences(updated);
    localStorage.setItem('agritrade_prefs', JSON.stringify(updated));
    
    // Sync with your Cloudflare Functions [[path]].js
    if (isAuthenticated) {
      try {
        await fetch('/api/user/preferences', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user?.id, ...updated })
        });
      } catch (err) {
        console.log("Offline: Preferences saved locally only.");
      }
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    updateUser,
    // --- NEW EXPORTS ---
    preferences,
    updatePreferences
  };

  return (
    <AuthContext.Provider value={value}>
      {/* Dynamic Theme Wrapper */}
      <div className={`min-h-screen transition-colors duration-300 ${preferences.theme === 'dark' ? 'bg-slate-900 text-white' : ''}`}>
        {children}
      </div>
    </AuthContext.Provider>
  );
};
